import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ConsumeSVG from '~/assets/Icons/consume';
import CalendarSVG from '~/assets/Icons/calendar';
import ReloadSVG from '~/assets/Icons/reload';
import { Margin } from '~/components/Margin';
import { statusColor } from '~/utils/invoiceStatus';
import { Breakpoints, Color } from '~/styles';
import { IState } from '~/store/modules/types';
import { IInvoices } from '~/store/modules/enrollmentsInvoices/types';
import { getEnrollmentsInvoicesDetailRequest } from '~/store/modules/enrollmentsInvoices/actions';
import { AppDispatch } from '~/store';
import {
  CardIColumn,
  CardInvoHeader,
  CardInvoiceBody,
  CardBorder,
  CardStatusText,
  CardIValueText,
  CCol,
  Container,
  ContainerAutomaticDebit,
  ContainerCard,
  ContainerInvoiceDetails,
  ContainerSeeInvoices,
  CRow,
  PayText,
  SeeInvoicesButton,
  Separator,
  ContainerCardHeader,
  CardDateText,
  CardLine,
  Button,
  DetailButton,
} from './styles';
import SkeletonCustom from '~/components/Skeleton';
import moment from 'moment';
import formatter from '~/utils/formatter';
import AutomaticDebitSVG from '~/assets/Icons/AutomaticDebit';
import { activateModalAutomaticDebits } from '~/store/modules/automaticDebits/actions';
import { existsAccountDebt } from '~/utils/verifyAccountExists';
import { ArrowButtonIcon } from '../../icon';

export default function NewCardInvoice({
  invoice_id,
  status,
  amount,
  billed_consumption,
  maturity_date,
  issue_date_hour,
}: IInvoices) {
  const isMobile = useMediaQuery({ maxWidth: Breakpoints.tablet.max });

  const dispatch = useDispatch<AppDispatch>();

  const loading = useSelector((item: IState) => item.enrollmentsInvoices.loading);
  const data = useSelector((item: IState) => item.automaticDebits.data);

  const detailInvoice = (paymentMethod: string) => {
    if (existsAccountDebt(data, maturity_date)) {
      dispatch(activateModalAutomaticDebits({ invoice_id }));
    } else {
      dispatch(getEnrollmentsInvoicesDetailRequest(invoice_id));
    }
  };

  return (
    <Container>
      {loading ? (
        <div style={{ width: '100%' }}>
          <SkeletonCustom height={isMobile ? 196 : 208} />
        </div>
      ) : (
        <ContainerCard>
          <CardBorder
            color={!invoice_id ? Color.gray : statusColor(status?.label, true)}
          />
          <CardInvoHeader>
            <ContainerCardHeader>
              <CardDateText>
                {issue_date_hour &&
                  `${format(new Date(issue_date_hour), 'MMMM', {
                    locale: ptBR,
                  })} de ${format(new Date(issue_date_hour), 'yyyy')}`}
              </CardDateText>
              <CardStatusText
                color={
                  !status?.label
                    ? Color.grayLightSeconde
                    : statusColor(status?.label, true)
                }
              >
                {status?.label ? status?.label?.toLowerCase() : 'Sem faturas'}
              </CardStatusText>
            </ContainerCardHeader>
          </CardInvoHeader>
          <CardLine />
          <CardInvoiceBody>
            <CardIColumn style={{ justifyContent: 'flex-start' }}>
              <div>
                <CardIValueText
                  isLoading={!invoice_id}
                  style={{
                    color: !invoice_id
                      ? Color.gray
                      : statusColor(status?.label, true),
                  }}
                  className={!invoice_id ? 'value-empty' : 'value'}
                >
                  {!invoice_id ? '-' : formatter.formatCurrency(amount)}
                </CardIValueText>
                <CRow>
                  <ConsumeSVG />
                  <CCol>
                    <span>Consumo:</span>
                    <span className="value">
                      {!invoice_id ? '-' : billed_consumption}
                    </span>
                  </CCol>
                </CRow>
                <Margin height={6} />
                <CRow>
                  <CalendarSVG />
                  <CCol>
                    <span>Vencimento:</span>
                    <span className="value">
                      {!invoice_id ? '-' : moment(maturity_date).format('DD/MM/YY')}
                    </span>
                  </CCol>
                </CRow>
              </div>
            </CardIColumn>
            {status?.label !== 'QUITADA' && status?.label !== 'PROCESSANDO' ? (
              <CardIColumn style={{ justifyContent: 'flex-start' }}>
                <PayText />
                <Margin height={8} />
                {!invoice_id ? (
                  <>
                    <Margin height={50} />
                    <ReloadSVG width="61" height="61" />
                    <Margin height={42} />
                  </>
                ) : (
                  <ContainerSeeInvoices>
                    <SeeInvoicesButton
                      type="button"
                      onClick={() => detailInvoice('')}
                    >
                      Pagar
                      <ArrowButtonIcon />
                    </SeeInvoicesButton>
                  </ContainerSeeInvoices>
                )}
              </CardIColumn>
            ) : (
              <ContainerSeeInvoices>
                <DetailButton type="button" onClick={() => detailInvoice('')}>
                  Ver fatura
                  <ArrowButtonIcon />
                </DetailButton>
              </ContainerSeeInvoices>
            )}
          </CardInvoiceBody>
          <CardLine />
          {invoice_id > 0 && (
            <ContainerInvoiceDetails>
              <Button onClick={() => detailInvoice('')}>Fatura completa</Button>
              {existsAccountDebt(data, maturity_date) && (
                <>
                  <Separator />
                  <ContainerAutomaticDebit>
                    <AutomaticDebitSVG color={Color.green} width={29} height={25} />
                    <Margin width={10} />
                    <p>
                      Débito em conta <br />
                      cadastrado
                    </p>
                  </ContainerAutomaticDebit>
                </>
              )}
            </ContainerInvoiceDetails>
          )}
        </ContainerCard>
      )}
    </Container>
  );
}
